(ns app.front.view.admin.tenant-management.create-view
  "System-admin tenant creation. Not a wizard: it asks only for what
  `:admin/tenant-create` cannot derive (identity, first products, first
  municipality) and then opens the new tenant's page, whose tabs cover the
  rest."
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.toggle-list :as ui-toggle-list]
            [app.front.util :refer [for-frag]]
            [app.front.view.admin.domain-groups.common :as domain-groups-common]
            [app.front.view.admin.domain-groups.municipality-categories :as municipality-categories]
            [app.front.view.admin.domain-groups.municipality-groups :as mg]
            [app.front.view.admin.domain-groups.subs :as domain-groups-subs]
            [app.front.view.admin.tenant-management.common :as tm-common]
            [app.front.view.admin.tenant-management.municipalities-tab :as municipalities-tab]
            [app.front.view.admin.tenant-management.products-tab :as products-tab]
            [app.front.view.admin.tenant-management.subs :as tm-subs]
            [app.front.view.group.admin.common :as acommon]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.domain.validators :as v]
            [core.front.carbon :as cc]
            [core.front.config :as config]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.i18n :as i18n]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Saving
;; ---------------------------------------------------------------------------

(rf/reg-event-fx ::create
  (fn [_ [_ {:keys [municipality] :as draft}]]
    {:comm/command {:command    :admin/tenant-create
                    :data       (-> draft
                                    (dissoc :municipality)
                                    (update :group-name str/trim)
                                    (update :group-number str/trim)
                                    (update :group-domain str/trim)
                                    (assoc :municipalities [municipality]))
                    :notify     {:success :admin/tenant-created}
                    :on-success [::create.success]}}))

(rf/reg-event-fx ::create.success
  (fn [_ [_ status {:keys [id changes]}]]
    {:fx [[:dispatch [::entities/apply-changes status changes]]
          ;; The new tenant's groups, scopes and municipality all arrive in the
          ;; changes, but re-pull anyway so the list query stays authoritative.
          [:dispatch [::tm-subs/fetch-tenants]]
          ;; Straight to the new tenant: products, features, users and further
          ;; municipalities are all edited there.
          [:dispatch [:route/push {:route  :view/admin.tenant-management.detail
                                   :params {:tenant-id id}}]]]}))

;; ---------------------------------------------------------------------------
;; Languages, in app-db rather than the draft: the localized inputs take a
;; zero-arg `:allowed-locales-sub`, which limits the municipality names to the
;; languages picked here.
;; ---------------------------------------------------------------------------

(rf/reg-event-db ::set-languages
  (fn [db [_ languages]]
    (assoc db ::languages languages)))

(rf/reg-sub ::languages
  (fn [db _]
    (or (not-empty (::languages db))
        #{i18n-model/default-lang})))

;; ---------------------------------------------------------------------------
;; Draft, in app-db. Until the first edit it is built from the countries and
;; currencies, so it follows them once they arrive.
;; ---------------------------------------------------------------------------

(defn- new-draft [countries currencies]
  (let [country (first (keys countries))]
    {:group-name   ""
     :group-number ""
     :group-domain ""
     :country-code country
     :subdivision  nil
     :currency     (first currencies)
     :timezone     nil
     :permit-types #{}
     :municipality {:name          {}
                    :official-name {}
                    :number        ""}}))

(defn- current-draft [db]
  (or (::draft db)
      (new-draft (::mg/countries db) (::mg/currencies db))))

(rf/reg-sub ::draft :-> current-draft)

(rf/reg-event-db ::update-draft
  (fn [db [_ f & args]]
    (assoc db ::draft (apply f (current-draft db) args))))

(rf/reg-event-db ::reset-draft
  (fn [db _]
    (dissoc db ::draft)))

(defn- update-draft!
  "Synchronously, so that text inputs keep their caret."
  [f & args]
  (rf/dispatch-sync (into [::update-draft f] args)))

(defn- municipality-name
  "The tenant's name as a localized-string map for its first municipality, in
  every language the tenant has, since the name is the same in all of them until
  someone says otherwise."
  [languages group-name]
  (if (str/blank? group-name)
    {}
    (zipmap languages (repeat group-name))))

(defn- set-detail
  "Sets one tenant field and, for the two the first municipality shares with it,
  keeps the municipality in step — but only while the municipality still holds
  the mirrored value, so typing there wins."
  [draft languages k v]
  (let [[municipality-key ->municipality] (case k
                                            :group-name   [:name (partial municipality-name languages)]
                                            :group-number [:number str/trim]
                                            [nil nil])]
    (cond-> (assoc draft k v)
      (and municipality-key
           (= (get-in draft [:municipality municipality-key])
              (->municipality (get draft k))))
      (assoc-in [:municipality municipality-key] (->municipality v)))))

(defn- subdivision-required?
  "Only countries that have subdivisions in the configuration need one."
  [countries {:keys [country-code]}]
  (boolean (seq (get countries country-code))))

;; ---------------------------------------------------------------------------
;; Geocoding, as the municipality-groups dialog's `::geocode-if-enough-data`:
;; once the name and region are known, prefill the group code, timezone and
;; municipality centre.
;; ---------------------------------------------------------------------------

(defn- geocode-result->draft
  "Fills in what the admin has not: the group code (which the municipality's own
  code mirrors), the timezone, and the municipality's centre. The centre is only
  placed once, so a later rename never moves a pin that has been dropped."
  [draft languages {:keys [coordinates municipality-number timezone]}]
  (cond-> draft
    (and municipality-number (str/blank? (:group-number draft)))
    (set-detail languages :group-number municipality-number)

    (and timezone (nil? (:timezone draft)))
    (assoc :timezone timezone)

    (and coordinates (nil? (get-in draft [:municipality :location])))
    (assoc-in [:municipality :location] coordinates)))

(rf/reg-event-fx ::geocode
  (fn [_ [_ query languages]]
    {:dispatch-debounce {:id      ::geocode
                         :timeout 300
                         :action  :dispatch
                         :event   [::domain-groups-subs/do-geocoding-request
                                   query
                                   [::geocoded languages]]}}))

(rf/reg-event-fx ::maybe-geocode
  ;; Only while the group code is empty, as the municipality-groups dialog does,
  ;; so a typed or looked-up code is never overwritten.
  (fn [{:keys [db]} [_ countries languages]]
    (let [{:keys [group-name group-number country-code subdivision] :as draft} (current-draft db)]
      (when (and (str/blank? group-number)
                 (>= (count (str/trim (str group-name))) 5)
                 country-code
                 (or subdivision (not (subdivision-required? countries draft))))
        {:dispatch [::geocode
                    (p/assoc-if {:search-text (str/trim group-name)
                                 :country     country-code}
                                :subdivision subdivision)
                    languages]}))))

(rf/reg-event-db ::geocoded
  (fn [db [_ languages _status response]]
    (assoc db ::draft (geocode-result->draft (current-draft db) languages response))))

(defn- valid?
  [countries coordinates {:keys [group-name group-number group-domain country-code
                                 subdivision currency timezone permit-types
                                 config-languages municipality]
                          :as   draft}]
  ;; `config-languages` is merged in from `::languages` before validating.
  (and (not (str/blank? (str/trim group-name)))
       (workspace-model/group-number? (str/trim group-number))
       (v/valid-domain? (str/trim group-domain))
       (some? country-code)
       (or (not (subdivision-required? countries draft)) (some? subdivision))
       (some? currency)
       (some? timezone)
       (seq permit-types)
       (seq config-languages)
       (domain-groups-common/valid-municipality? coordinates municipality)))

;; ---------------------------------------------------------------------------
;; Form sections
;; ---------------------------------------------------------------------------

(defn- details-fields [tr countries]
  (let [{:keys [group-name group-number group-domain country-code subdivision currency timezone]}
        (<sub [::draft])

        languages    (<sub [::languages])
        subdivisions (seq (get countries country-code))
        geocode!     #(>evt [::maybe-geocode countries languages])]
    ;; Three to a row from lg up: the tenant's identity on the first row, the
    ;; rest of its settings on the second.
    [:div.grid.gap-4 {:class "sm:grid-cols-2 lg:grid-cols-3"}
     [ui/text-input {:id          "new-tenant-group-name"
                     :label-text  (tr [:admin/group-name])
                     :placeholder (tr [:placeholder/group])
                     :required    true
                     :value       group-name
                     :on-change   #(do (update-draft! set-detail languages :group-name (-> % .-target .-value))
                                       (geocode!))}]
     [ui-select/select {:id           "new-tenant-country"
                        :label-text   (tr [:admin/country-code])
                        :required     true
                        :values       (vec (keys countries))
                        :value        country-code
                        :val->str     #(tr [(keyword "country" (name %))])
                        :allow-empty? false
                        ;; Subdivisions are country-specific, so a country change
                        ;; invalidates the current one.
                        :on-change    #(do (update-draft! assoc :country-code % :subdivision nil)
                                           (geocode!))}]
     (when subdivisions
       [ui-select/select {:id          "new-tenant-subdivision"
                          :label-text  (tr [:admin/subdivision])
                          :required    true
                          :values      (vec subdivisions)
                          :value       subdivision
                          :val->str    #(municipality-model/subdivision-name tr %)
                          :select-text (tr [:none])
                          :on-change   #(do (update-draft! assoc :subdivision %)
                                            (geocode!))}])
     [ui/text-input {:id           "new-tenant-group-number"
                     :label-text   (tr [:admin/group-id])
                     :placeholder  (tr [:placeholder/municipality-code])
                     :pattern      workspace-model/group-number-pattern
                     :required     true
                     :value        group-number
                     :invalid      (and (not (str/blank? group-number))
                                        (not (workspace-model/group-number? (str/trim group-number))))
                     :invalid-text (tr [:admin/group-number-invalid])
                     :on-change    #(update-draft! set-detail languages :group-number (-> % .-target .-value))}]
     [ui/text-input {:id           "new-tenant-group-domain"
                     :label-text   (tr [:admin/group-domain-label])
                     :placeholder  (tr [:admin/placeholder-domain])
                     :required     true
                     :value        group-domain
                     :invalid      (and (not (str/blank? group-domain))
                                        (not (v/valid-domain? (str/trim group-domain))))
                     :invalid-text (tr [:admin/invalid-domain])
                     :on-change    #(update-draft! assoc :group-domain (-> % .-target .-value))}]
     [ui-select/select {:id           "new-tenant-currency"
                        :label-text   (tr [:admin/currency])
                        :required     true
                        :values       (vec (<sub [::mg/currencies]))
                        :value        currency
                        :val->str     str
                        :allow-empty? false
                        :on-change    #(update-draft! assoc :currency %)}]
     [ui-select/select {:id          "new-tenant-timezone"
                        :label-text  (tr [:admin/timezone])
                        :required    true
                        :values      (vec (<sub [::mg/timezones]))
                        :value       timezone
                        :val->str    str
                        :select-text (tr [:none])
                        :on-change   #(update-draft! assoc :timezone %)}]]))

(defn- language-checkboxes
  "The languages of the tenant's permit-type configuration and municipality
  names. Asked here because they cannot be changed later."
  [tr]
  (let [selected (<sub [::languages])
        locales  (<sub [::i18n/locales])]
    (when (seq locales)
      [:div
       [:div.bx--label (tr [:admin/languages-for-permit-types])]
       [:div.flex.flex-wrap.gap-x-6
        (for-frag [[locale names] locales]
          (cc/checkbox {:id         (str "new-tenant-language-" (name locale))
                        :label-text (:name names)
                        :checked    (contains? selected locale)
                        ;; At least one language must stay selected.
                        :disabled   (= selected #{locale})
                        :on-change  #(>evt [::set-languages
                                            ((if % conj disj) selected locale)])}))]])))

(defn- default-authority-group-name
  "The name the backend would give the product's authority group, from the same
  `app.domain.group.model` helper it uses."
  [tr group-name permit-type]
  (when-not (str/blank? group-name)
    (group-model/default-authority-group-name tr (str/trim group-name) permit-type)))

(defn- product-rows
  [tr {:keys [group-name catalogue selected categories authority-group-names]}]
  (->> (keys catalogue)
       tm-common/products
       (map (fn [permit-type]
              (let [default-name (default-authority-group-name tr group-name permit-type)]
                {:permit-type          permit-type
                 :name                 (tm-common/product-name tr permit-type)
                 :selected?            (contains? selected permit-type)
                 :default-group-name   default-name
                 ;; An override sticks even when the tenant is renamed; without
                 ;; one the name follows the tenant.
                 :authority-group-name (or (get authority-group-names permit-type)
                                           default-name)
                 :name-overridden?     (contains? authority-group-names permit-type)
                 :categories           (get categories permit-type #{})
                 :available-categories (->> (get catalogue permit-type)
                                            (sort-by (comp tr vector))
                                            vec)})))
       (sort-by :name)
       vec))

(defn- authority-group-name-input
  "The name the product's authority group will be created with. Prefilled with
  the default, which keeps following the tenant's name until it is changed here."
  [tr {:keys [permit-type default-group-name authority-group-name name-overridden?]}]
  [:div.max-w-lg
   [ui/text-input
    {:id          (str "new-tenant-ag-name-" (name permit-type))
     :label-text  (tr [:admin/tenant-authority-group])
     :placeholder default-group-name
     :helper-text (when-not name-overridden?
                    (tr [:admin/tenant-authority-group-name-help]))
     :value       authority-group-name
     :on-change   (fn [e]
                    (let [typed    (-> e .-target .-value)
                          override (fn [names]
                                     ;; Back to the default is the same as never having
                                     ;; overridden it, so the name follows the tenant again.
                                     (if (or (str/blank? typed) (= typed default-group-name))
                                       (dissoc names permit-type)
                                       (assoc names permit-type typed)))]
                      (update-draft! update :authority-group-names override)))}]])

(defn- product-editor
  "Row expansion: which categories the product starts with, and the name its
  authority group will get."
  [tr {:keys [permit-type selected? categories available-categories] :as row}]
  [:div.p-2
   [authority-group-name-input tr row]
   [products-tab/category-checkboxes
    {:tr                   tr
     :permit-type          permit-type
     :available-categories available-categories
     :selected             categories
     :enabled?             selected?
     :on-toggle            (fn [category checked?]
                             (update-draft! update-in [:categories permit-type]
                                            (fnil (if checked? conj disj) #{}) category))}]
   (when-not selected?
     (ui/help (tr [:admin/tenant-select-product-help])))])

(defn- product-cell [{:keys [permit-type name]}]
  [:div.flex.items-center.gap-2
   (when-let [icon (ui/permit-type-icon permit-type)]
     [:> icon {:class-name "block shrink-0"
               :width      "16"
               :height     "16"}])
   [:span.font-medium name]])

(defn- select-product!
  "Selecting a product for the first time takes the platform's default
  categories, as enabling one on the Products tab does."
  [{:keys [permit-type available-categories]} selected?]
  (update-draft! (fn [draft]
                   (cond-> (update draft :permit-types (if selected? conj disj) permit-type)
                     (and selected? (not (contains? (:categories draft) permit-type)))
                     (assoc-in [:categories permit-type]
                               (set (ptc-model/filter-default-category-seq permit-type
                                                                           available-categories)))))))

(defn- products-table
  "Always in selection mode: a checked row is a product the tenant starts with.
  Rows expand for the categories and the authority group name."
  [tr rows]
  (let [row-by-key (into {} (map (juxt (comp name :permit-type) identity) rows))]
    [sortable-table/table
     {:tr               tr
      :id               "new-tenant-products-table"
      :items            rows
      :id-func          (comp name :permit-type)
      :initial-sorting  [:name :asc]
      :perma-selecting? true
      :selection        (->> rows (filter :selected?) (map (comp name :permit-type)) set)
      :on-select        (fn [checked? key]
                          (select-product! (get row-by-key key) checked?))
      :get-status       (fn [{:keys [selected?]}] (when selected? :ok))
      :expanded-func    (fn [row] [product-editor tr row])
      :cols             [[:admin/products
                          (fn [row] [product-cell row])
                          {:sorting (fn [a b] (compare (:name a) (:name b)))}]
                         [:admin/tenant-authority-group
                          :authority-group-name
                          {:truncate? true}]
                         [:categories
                          (fn [{:keys [selected? categories]}]
                            (when selected?
                              (str (count categories))))
                          {:width "120px"}]]}]))

(defn- skip-config-toggle
  "As in the municipality-groups dialog, for tenants whose default configuration
  a separate process creates. Offered only when the backend's flag for it is on."
  [tr]
  (when (ff/enabled? :feature-flag.id/skip-config-creation-for-new-groups)
    (let [production? (= (<sub [::config/env-name]) "prod")
          skip?       (or production? (boolean (:skip-default-configuration? (<sub [::draft]))))]
      [:div.mt-6
       [ui-toggle-list/toggle-list
        {:items [{:id          "skip-default-config"
                  :label       (tr [:admin/skip-default-config-creation])
                  :description (when production?
                                 (tr [:admin/skip-default-config-always-in-production]))
                  :toggled?    skip?
                  :disabled?   production?
                  :on-toggle   #(update-draft! assoc :skip-default-configuration? %)}]}]])))

(defn- products-section [tr]
  (let [draft     (<sub [::draft])
        catalogue (<sub [::municipality-categories/permit-type->categories nil])
        rows      (product-rows tr {:group-name            (:group-name draft)
                                    :catalogue             catalogue
                                    :selected              (:permit-types draft)
                                    :categories            (:categories draft)
                                    :authority-group-names (:authority-group-names draft)})]
    [:div
     (ui/help (tr [:admin/tenant-first-products-help]))
     (if (seq rows)
       [products-table tr rows]
       (cc/inline-loading {:description (tr [:loading])}))
     [skip-config-toggle tr]]))

(defn- municipality-section
  "The tenant's first municipality. Its name and code follow the tenant's own —
  the common case for a single-municipality tenant — until they are edited here."
  [tr]
  ;; `municipality-fields` edits an atom, here a cursor onto the draft.
  (r/with-let [*municipality (r/cursor (fn
                                         ([path] (get-in (<sub [::draft]) path))
                                         ([path value] (update-draft! assoc-in path value)))
                                       [:municipality])]
    [:div
     [:p.text-gray-600.mb-4 (tr [:admin/tenant-first-municipality-help])]
     [municipalities-tab/municipality-fields
      {:tr            tr
       :*draft        *municipality
       :languages-sub ::languages
       :id-prefix     "new-tenant-muni"}]]))

;; ---------------------------------------------------------------------------
;; Page
;; ---------------------------------------------------------------------------

(defn- card [tr id heading & body]
  (into [section/section id {:tr tr :always-expanded? true :class "!my-0"} heading]
        body))

(defn- form [tr]
  (let [countries (<sub [::mg/countries])
        draft     (assoc (<sub [::draft]) :config-languages (<sub [::languages]))
        saving?   (<sub [:comm/command-processing? :admin/tenant-create])]
    [:div.flex.flex-col.gap-4.mt-6
     [card tr :new-tenant-details :admin/tenant-details
      [details-fields tr countries]
      [:div.mt-4 [language-checkboxes tr]]]
     [card tr :new-tenant-products :admin/products
      [products-section tr]]
     [card tr :new-tenant-municipality :admin/tenant-municipalities
      [municipality-section tr]]
     [:div.flex.justify-end.gap-2
      [ui/button {:kind :secondary
                  :href (href/href :view/admin.tenant-management)}
       (tr [:cancel])]
      [ui/button {:kind     :primary
                  :icon     i/plus
                  :disabled (or saving?
                                (not (valid? countries
                                             (<sub [::domain-groups-subs/coordinates])
                                             draft)))
                  :on-click #(>evt [::create draft])}
       (tr [:admin/create-tenant])]]]))

(defn view [match]
  (let [tr        (<sub [:tr])
        countries (<sub [::mg/countries])]
    [acommon/admin-page-v2
     {:match   match
      :title   :view/admin.tenant-management
      :heading [:div.my-10
                [:h1.m-0.font-semibold (tr [:admin/create-tenant])]
                [:p.text-gray-600.mt-1.pb-0 (tr [:admin/create-tenant-help])]]}
     ;; Countries, timezones and currencies arrive with the tenant list query.
     (if (seq countries)
       [form tr]
       [:div.py-8 (cc/inline-loading {:description (tr [:loading])})])]))

(def route
  {:name        :view/admin.tenant-management.create
   :view        view
   :own-layout? true
   ;; `/new` and `/:tenant-id` are siblings; both opt out of reitit's
   ;; conflicting-path check, and the literal one is listed first so it wins.
   :conflicting true
   :breadcrumb  {:items [:view/admin.tenant-management]}
   :view-sub    [::tm-subs/detail-view-data]
   :controllers [{:start (fn [_]
                           (>evt [::reset-draft])
                           (>evt [::set-languages #{i18n-model/default-lang}])
                           ;; For the countries, timezones, currencies and
                           ;; permit-type catalogue the form is built from.
                           (>evt [::tm-subs/fetch-tenants]))}]})
